import { useQuery } from '@apollo/client';
import {
  Center,
  Flex,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { AccordionPanel } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  AlertWithSessionSummary,
  IQueryAlertsPaginated,
  QUERY_ALERTS_NOTICE_PAGINATED,
  QUERY_ALERTS_NOTICE_SUMMARY,
  SessionSummaryData,
} from '@investigator/features/detections/queries';
import { buildAlertIdQuery } from '../../utils/buildAlertIdQuery';
import moment from 'moment';
import { useContext, useState } from 'react';
import { useHumioToken } from '@investigator/shared/providers';

import { TimestampCell } from '@investigator/shared/components';
import { i18n } from '@investigator/shared/localization';
import {
  DetailsGrid,
  DetailsGridRow,
} from '../../../../../../../../../../components';
import { SanitizedNoticeAlert } from '../../../DetectionAlerts';
import { InvestigateLogsTableModal } from '../InvestigateLogsTableModal';
import { LogscaleDetails } from '../LogscaleDetails';
import { buildSearchQueryString } from '../LogscaleDetails/utils/buildSearchQueryString';
import { LogscaleMenuButton } from '../LogscaleMenuButton';
import { SessionSummary } from '../SessionSummary';
import { useDetectionContext } from '@investigator/features/detections/context';
import { AppConfigurationsContext } from '@investigator/shared/providers';

const { alerts, detection } = i18n.pages.detections;

interface AlertRowNoticeProps {
  item: SanitizedNoticeAlert;
  logScaleQuery?: string;
  showContent?: boolean;
}

interface AlertRowSplitNoticeProps {
  item: SanitizedNoticeAlert;
  loading: boolean | undefined;
  isGPTPrivateDataEnabled: boolean;
  logScaleQuery?: string;
}

// Local type for alert_info with severity and is_custom_severity
interface AlertInfoWithSeverity {
  alert_type?: string;
  alert_name?: string;
  severity?: number;
  is_custom_severity?: boolean;
}

export const NoticeButtonsCol = ({
  item,
  showContent,
  logScaleQuery,
}: AlertRowNoticeProps) => {
  const {
    isOpen: investigateModalIsOpen,
    onOpen: investigateModalOnOpen,
    onClose: investigateModalOnClose,
  } = useDisclosure();
  const formattedTime = moment
    .unix(item.alert_timestamp?.observed ?? 0)
    .format();
  const start = moment(formattedTime).subtract(1, 'hours').unix();
  const end = moment(formattedTime).add(24, 'hours').unix();

  // Map SanitizedNoticeAlert to DetectionAlertItem
  const detectionAlertItem = {
    ...item,
    alert_name: item?.alert_info?.alert_name ?? '',
    severity: Number(
      (item?.alert_info as AlertInfoWithSeverity)?.severity ?? 0,
    ),
    is_custom_severity: Boolean(
      (item?.alert_info as AlertInfoWithSeverity)?.is_custom_severity,
    ),
    alert_timestamp: { observed: item?.alert_timestamp?.observed ?? 0 },
    alert_info: {
      alert_type: item?.alert_info?.alert_type ?? '',
      alert_name: item?.alert_info?.alert_name ?? '',
    },
    alert_entity: {
      entity_name: item?.alert_entity?.entity_name ?? '',
      entity_type: item?.alert_entity?.entity_type ?? '',
    },
    first_seen: 0,
    last_seen: 0,
  };

  const searchQueryString = buildSearchQueryString({
    item: detectionAlertItem,
    logScaleQuery,
    start,
    end,
  });

  return (
    <Flex flexGrow={1}>
      <LogscaleMenuButton
        searchQueryString={searchQueryString}
        investigateModalOnOpen={investigateModalOnOpen}
        showContent={showContent}
      />
      <InvestigateLogsTableModal
        isOpen={investigateModalIsOpen}
        onClose={investigateModalOnClose}
        onOpen={investigateModalOnOpen}
        alertsName={item?.alert_info?.alert_name ?? ''}
        alertsUid={item?.uid}
        alertsTimestamp={formattedTime}
        alertsTenant={item?.tenant}
        logScaleQuery={logScaleQuery}
      />
    </Flex>
  );
};

export const AlertRowNotice = ({
  item,
  logScaleQuery,
}: AlertRowNoticeProps) => {
  return (
    <HStack spacing={4}>
      <VStack alignItems="start">
        <TimestampCell
          timestamp={item?.alert_timestamp?.observed ?? 0}
          isFromNow={false}
        />
        <HStack color="text.200">
          <Text textStyle="body-md">{item?.alert_info?.alert_name}</Text>
          <Text textStyle="body-md">|</Text>
          <Text textStyle="body-md">{item?.alert_entity?.entity_name}</Text>
        </HStack>
      </VStack>
      <NoticeButtonsCol item={item} logScaleQuery={logScaleQuery} />
    </HStack>
  );
};

export const AlertRowSplitNotice = ({
  item,
  loading,
  isGPTPrivateDataEnabled,
  logScaleQuery,
}: AlertRowSplitNoticeProps) => {
  const { appConfigurations } = useContext(AppConfigurationsContext);
  const { token: sessionToken } = useHumioToken();
  const { detectionData } = useDetectionContext();
  const [sessionSummaryData, setSessionSummaryData] = useState<
    SessionSummaryData | undefined
  >();
  const time = moment.unix(item?.alert_timestamp?.observed ?? 0);
  const formattedTime = time.format();
  const isLLMSummary = appConfigurations?.feature_flags.llm_summary || false;

  const query =
    isLLMSummary && isGPTPrivateDataEnabled
      ? QUERY_ALERTS_NOTICE_SUMMARY
      : QUERY_ALERTS_NOTICE_PAGINATED;

  const noFilterVariables = {
    query: buildAlertIdQuery(item.alert_id),
    log_search_client_session_id: sessionToken || '',
  };

  const { loading: sessionSummaryLoading } = useQuery<IQueryAlertsPaginated>(
    query,
    {
      skip:
        !detectionData ||
        !detectionData?.tenant_info?.tenant_id ||
        !detectionData?.alert_entity?.entity_id ||
        !item?.uid,
      variables: noFilterVariables,
      onCompleted: (data) => {
        const sessionSummary = (
          data?.queryAlertsPaginated?.alerts?.[0] as AlertWithSessionSummary
        )?.session_summary;
        setSessionSummaryData(sessionSummary);
      },
    },
  );

  if (loading) {
    return (
      <Center w="100%">
        <GraphQLReqStatus loading={loading} />
      </Center>
    );
  }
  return (
    <VStack mb={2} w="100%" alignItems="start">
      <LogscaleDetails
        alertsUid={item?.uid}
        alertsName={item?.alert_info?.alert_name ?? ''}
        alertsTimestamp={formattedTime}
        alertsTenant={item?.tenant}
        logScaleQuery={logScaleQuery}
        alertType="notice"
        childrenBelowHeader={
          <AccordionPanel
            title={detection.alertDetails}
            data-testid="notice-alerts-wrapper"
            reduceMotion
          >
            <DetailsGrid>
              <DetailsGridRow title={alerts.name}>
                <Text overflowWrap="anywhere">
                  {item?.alert_info?.alert_name}
                </Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.timestamp}>
                <Text>{formattedTime}</Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.uid}>
                <Text>{item?.uid}</Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.sourceIp}>
                <VStack alignItems="start" spacing={1}>
                  {item?.source_entities &&
                    item.source_entities.length > 0 &&
                    item.source_entities.map((sourceIp) => (
                      <Text>{sourceIp?.entity_name}</Text>
                    ))}
                </VStack>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.destinationIp}>
                <VStack alignItems="start" spacing={1}>
                  {item?.destination_entities &&
                    item.destination_entities.length > 0 &&
                    item.destination_entities.map((destinationIp) => (
                      <Text>{destinationIp?.entity_name}</Text>
                    ))}
                </VStack>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.type}>
                <Text>{item?.alert_info?.alert_type}</Text>
              </DetailsGridRow>
            </DetailsGrid>
            {isLLMSummary && isGPTPrivateDataEnabled && (
              <SessionSummary
                sessionSummaryData={sessionSummaryData}
                loading={sessionSummaryLoading}
              />
            )}
          </AccordionPanel>
        }
      />
    </VStack>
  );
};
